import { getConfigManager } from '../../config';
import { Decimal, ZERO } from '../../datastructures';
import { Asset, AssetConfig, AssetTier } from '../../interfaces';
import debtDataAtom, { DebtDataMap } from './debtDataAtom';
import interestRateConfigAtom, { InterestRateConfigMap } from './interestRateConfigAtom';
import collateralDataAtom, { CollateralDataMap } from './collateralDataAtom';
import interestStateAtom, { InterestStateMap } from './interestStateAtom';
import assetRewardsApyAtom, { AssetRewardsApyMap } from './assetRewardsApyAtom';
import nostraTokenTotalSupplyAtom, { NostraTokenTotalSupplyMap } from './nostraTokenTotalSupplyAtom';
import nostraTokenTotalSupplyCapAtom, { NostraTokenTotalSupplyCapMap } from './nostraTokenTotalSupplyCapAtom';
import { createAsyncAtom } from '../../lib/state';

interface AssetData {
  assetTier: AssetTier;
  totalSupply: Decimal;
  nostraInterestBearingSupply: Decimal;
  nostraInterestBearingCollateralSupply: Decimal;
  nostraSupply: Decimal;
  nostraCollateralSupply: Decimal;
  debtTokenSupply: Decimal;
  nostraInterestBearingSupplyCap: Decimal;
  nostraInterestBearingCollateralSupplyCap: Decimal;
  nostraSupplyCap: Decimal;
  nostraCollateralSupplyCap: Decimal;
  debtTokenSupplyCap: Decimal;
  currentLendingRate: Decimal;
  currentBorrowRate: Decimal;
  optimalUtilizationRate: Decimal;
  baseBorrowRate: Decimal;
  rateSlope1: Decimal;
  rateSlope2: Decimal;
  generalProtocolFee: Decimal;
  collateralFactor: Decimal;
  debtTokenAddress: string;
  debtFactor: Decimal;
  defiSpringRewardsApy: Decimal;
}

const fetchData = (
  asset: AssetConfig,
  debtData: DebtDataMap,
  interestRateConfig: InterestRateConfigMap,
  collateralData: CollateralDataMap,
  interestState: InterestStateMap,
  assetRewardsApyMap: AssetRewardsApyMap,
  nostraTokenTotalSupply: NostraTokenTotalSupplyMap,
  nostraTokenTotalSupplyCap: NostraTokenTotalSupplyCapMap,
): AssetData => {
  return {
    assetTier: asset.assetTier,
    totalSupply: nostraTokenTotalSupply[asset.nostraInterestBearingTokenAddress]
      .add(nostraTokenTotalSupply[asset.nostraInterestBearingCollateralTokenAddress])
      .add(nostraTokenTotalSupply[asset.nostraTokenAddress])
      .add(nostraTokenTotalSupply[asset.nostraCollateralTokenAddress]),
    nostraInterestBearingSupply: nostraTokenTotalSupply[asset.nostraInterestBearingTokenAddress],
    nostraInterestBearingCollateralSupply: nostraTokenTotalSupply[asset.nostraInterestBearingCollateralTokenAddress],
    nostraSupply: nostraTokenTotalSupply[asset.nostraTokenAddress],
    nostraCollateralSupply: nostraTokenTotalSupply[asset.nostraCollateralTokenAddress],
    debtTokenSupply: nostraTokenTotalSupply[asset.debtTokenAddress],
    nostraInterestBearingSupplyCap: nostraTokenTotalSupplyCap[asset.nostraInterestBearingTokenAddress],
    nostraInterestBearingCollateralSupplyCap:
      nostraTokenTotalSupplyCap[asset.nostraInterestBearingCollateralTokenAddress],
    nostraSupplyCap: nostraTokenTotalSupplyCap[asset.nostraTokenAddress],
    nostraCollateralSupplyCap: nostraTokenTotalSupplyCap[asset.nostraCollateralTokenAddress],
    debtTokenSupplyCap: nostraTokenTotalSupplyCap[asset.debtTokenAddress],
    currentLendingRate: interestState[asset.debtTokenAddress].lendingRate.add(assetRewardsApyMap[asset.address]),
    currentBorrowRate: interestState[asset.debtTokenAddress].borrowRate,
    optimalUtilizationRate: interestRateConfig[asset.debtTokenAddress].optimalUtilizationRate,
    baseBorrowRate: interestRateConfig[asset.debtTokenAddress].baseBorrowRate,
    rateSlope1: interestRateConfig[asset.debtTokenAddress].rateSlope1,
    rateSlope2: interestRateConfig[asset.debtTokenAddress].rateSlope2,
    generalProtocolFee: interestRateConfig[asset.debtTokenAddress].generalProtocolFee,
    collateralFactor: collateralData[asset.address].collateralFactor,
    debtTokenAddress: asset.debtTokenAddress,
    debtFactor: debtData[asset.debtTokenAddress].debtFactor,
    defiSpringRewardsApy: assetRewardsApyMap[asset.address],
  };
};

const processAssetData = (assetConfig: AssetConfig, assetData: AssetData): Asset => {
  const {
    address,
    ticker,
    tokenPrecision,
    uiTokenPrecision,
    fiatTokenPrecision,
    nostraInterestBearingTokenAddress,
    nostraInterestBearingCollateralTokenAddress,
    nostraTokenAddress,
    nostraCollateralTokenAddress,
    debtTokenAddress,
    nostraInterestBearingTokenSymbol,
    nostraInterestBearingCollateralTokenSymbol,
    nostraTokenSymbol,
    nostraCollateralTokenSymbol,
    debtTokenSymbol,
    hidden,
  } = assetConfig;
  const {
    assetTier,
    currentLendingRate,
    currentBorrowRate,
    optimalUtilizationRate,
    baseBorrowRate,
    rateSlope1,
    rateSlope2,
    generalProtocolFee,
    totalSupply,
    collateralFactor,
    debtFactor,
    nostraInterestBearingSupply,
    nostraInterestBearingCollateralSupply,
    nostraCollateralSupply,
    nostraSupply,
    debtTokenSupply,
    nostraInterestBearingSupplyCap,
    nostraInterestBearingCollateralSupplyCap,
    nostraCollateralSupplyCap,
    nostraSupplyCap,
    debtTokenSupplyCap,
    defiSpringRewardsApy,
  } = assetData;
  const availableForBorrowing = Decimal.max(
    assetData.nostraInterestBearingSupply
      .add(assetData.nostraInterestBearingCollateralSupply)
      .sub(assetData.debtTokenSupply),
    ZERO,
  );

  let utilizationRate;
  if (assetData.debtTokenSupply.equals(ZERO)) {
    utilizationRate = ZERO;
  } else {
    utilizationRate = assetData.debtTokenSupply.div(
      assetData.nostraInterestBearingSupply.add(assetData.nostraInterestBearingCollateralSupply),
    );
  }

  return {
    address,
    token: ticker,
    tokenPrecision,
    uiTokenPrecision,
    fiatTokenPrecision,
    nostraInterestBearingTokenAddress,
    nostraInterestBearingCollateralTokenAddress,
    nostraTokenAddress,
    nostraCollateralTokenAddress,
    debtTokenAddress,
    nostraInterestBearingTokenSymbol,
    nostraInterestBearingCollateralTokenSymbol,
    nostraTokenSymbol,
    nostraCollateralTokenSymbol,
    debtTokenSymbol,
    assetTier,
    supplyApy: currentLendingRate,
    borrowApr: currentBorrowRate,
    optimalUtilizationRate,
    baseBorrowRate,
    rateSlope1,
    rateSlope2,
    generalProtocolFee,
    totalSupply,
    totalBorrow: debtTokenSupply,
    availableForBorrowing,
    collateralFactor,
    debtFactor,
    nostraInterestBearingSupply,
    nostraInterestBearingCollateralSupply,
    nostraCollateralSupply,
    nostraSupply,
    debtTokenSupply,
    nostraInterestBearingSupplyCap,
    nostraInterestBearingCollateralSupplyCap,
    nostraCollateralSupplyCap,
    nostraSupplyCap,
    debtTokenSupplyCap,
    utilizationRate,
    defiSpringRewardsApy,
    hidden: hidden ?? false,
  };
};

const fetchAssets = async (
  assetConfigList: AssetConfig[],
  debtDataMap: DebtDataMap,
  interestRateConfig: InterestRateConfigMap,
  collateralData: CollateralDataMap,
  interestState: InterestStateMap,
  assetRewardsApyMap: AssetRewardsApyMap,
  nostraTokenTotalSupply: NostraTokenTotalSupplyMap,
  nostraTokenTotalSupplyCap: NostraTokenTotalSupplyCapMap,
): Promise<Asset[]> => {
  return Promise.all(
    assetConfigList.map(async assetConfig => {
      const assetData = fetchData(
        assetConfig,
        debtDataMap,
        interestRateConfig,
        collateralData,
        interestState,
        assetRewardsApyMap,
        nostraTokenTotalSupply,
        nostraTokenTotalSupplyCap,
      );

      return processAssetData(assetConfig, assetData);
    }),
  );
};

const assetsAtom = createAsyncAtom(async get => {
  const debtData = await get(debtDataAtom);
  const interestRateConfig = await get(interestRateConfigAtom);
  const collateralData = await get(collateralDataAtom);
  const assetRewardsApy = await get(assetRewardsApyAtom);
  const nostraTokenTotalSupplyCap = await get(nostraTokenTotalSupplyCapAtom);
  const interestState = await get(interestStateAtom);
  const nostraTokenTotalSupply = await get(nostraTokenTotalSupplyAtom);

  const configAssets = Object.values(getConfigManager().getConfig().assets).filter(asset => !asset.hidden);

  if (
    !debtData ||
    !interestRateConfig ||
    !collateralData ||
    !interestState ||
    !assetRewardsApy ||
    !nostraTokenTotalSupply ||
    !nostraTokenTotalSupplyCap
  ) {
    return null;
  }

  return fetchAssets(
    configAssets,
    debtData,
    interestRateConfig,
    collateralData,
    interestState,
    assetRewardsApy,
    nostraTokenTotalSupply,
    nostraTokenTotalSupplyCap,
  );
});
export default assetsAtom;
